import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { type FeatureFlagRecord, rolloutDescriptions } from "./types";

export function RolloutPreview({ featureFlags }: { featureFlags: FeatureFlagRecord[] }) {
  const { flags: resolvedFlags, loading: resolvedFlagsLoading } = useFeatureFlags();
  return (
    <Card className="shadow-soft">
      <CardHeader><CardTitle>Rollout preview</CardTitle></CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {resolvedFlagsLoading && Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
        {!resolvedFlagsLoading && featureFlags.map((flag) => {
          const liveEnabled = Boolean(resolvedFlags[flag.key]);
          return (
            <div key={`preview-${flag.id}`} className="rounded-md border border-border px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{flag.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{flag.key}</p>
                </div>
                <Badge variant={liveEnabled ? "default" : "outline"}>{liveEnabled ? "Live for me" : "Hidden for me"}</Badge>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{rolloutDescriptions[flag.rollout_mode]}</p>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export function FlagStats({ stats }: { stats: { enabledCount: number; allowlistCount: number; targetedCount: number } }) {
  return (
    <section className="grid gap-3 md:grid-cols-3">
      <div className="rounded-md border border-border bg-background px-4 py-3">
        <p className="text-sm font-medium">Flags live now</p>
        <p className="mt-1 text-2xl font-semibold">{stats.enabledCount}</p>
        <p className="text-xs text-muted-foreground">Master switch turned on.</p>
      </div>
      <div className="rounded-md border border-border bg-background px-4 py-3">
        <p className="text-sm font-medium">Allowlist rollouts</p>
        <p className="mt-1 text-2xl font-semibold">{stats.allowlistCount}</p>
        <p className="text-xs text-muted-foreground">Flags using selected users or workspaces.</p>
      </div>
      <div className="rounded-md border border-border bg-background px-4 py-3">
        <p className="text-sm font-medium">Targets assigned</p>
        <p className="mt-1 text-2xl font-semibold">{stats.targetedCount}</p>
        <p className="text-xs text-muted-foreground">All user and workspace entries across flags.</p>
      </div>
    </section>
  );
}
